import React from "react";
import { ArrowUpRight, BarChart3, Boxes, ShoppingBag, Sparkles } from "lucide-react";
import PhoneFrame from "./PhoneFrame";
import { MOCKUP_SCREEN_ORDER } from "@/constants/mockups";
import { WEB_REGISTER_URL } from "@/constants/site";
import { captureEvent } from "@/lib/analytics";

const WEB_VIEWS = [
  { icon: BarChart3, label: "Dashboard", detail: "Daily pulse and performance" },
  { icon: ShoppingBag, label: "Sell", detail: "Fast point-of-sale" },
  { icon: Boxes, label: "Inventory", detail: "Products and stock control" },
  { icon: Sparkles, label: "Assistant", detail: "Answers from business data" },
];

export const ProductScreens = () => {
  return (
    <section id="product-screens" className="bg-[#F7FAF9] py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="max-w-3xl">
          <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-[#D96C4A]">One business, two workspaces</span>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold leading-[1.1] text-[#0A2424] tracking-tight">
            Move from the counter to the big picture without losing context.
          </h2>
          <p className="mt-5 text-base md:text-lg text-[#3a4a4a]">
            Use the mobile app for fast, offline-ready work on the shop floor. Open the web app when you want more space to review operations, manage records, and plan what happens next.
          </p>
        </div>

        <figure className="mt-12">
          <div className="relative overflow-hidden rounded-[2rem] border border-[#0E6B6B]/10 bg-[#ECEDEA] p-2 shadow-[0_32px_90px_-48px_rgba(10,36,36,0.7)] sm:p-3">
            <img
              src="/mockups/web-workspace-showcase.webp"
              alt="Shopbook web workspace showing the dashboard, point of sale, inventory, and business assistant"
              className="block h-auto w-full rounded-[1.55rem]"
              loading="lazy"
            />
            <a
              href={WEB_REGISTER_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => captureEvent("conversion_cta_clicked", { placement: "product_showcase", destination: "web_register" })}
              className="absolute bottom-5 right-5 hidden items-center gap-2 rounded-full bg-[#0A3B3B] px-5 py-3 text-sm font-semibold text-white shadow-xl transition-all hover:-translate-y-0.5 hover:bg-[#0E6B6B] sm:inline-flex"
            >
              Open the web app <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
          <figcaption className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {WEB_VIEWS.map(({ icon: Icon, label, detail }) => (
              <div key={label} className="flex items-center gap-3 rounded-2xl border border-[#0E6B6B]/8 bg-white px-4 py-3">
                <div className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-[#0E6B6B]/8 text-[#0E6B6B]"><Icon className="h-4 w-4" /></div>
                <div>
                  <div className="text-sm font-semibold text-[#0A2424]">{label}</div>
                  <div className="text-[11px] text-[#4B5E5E]">{detail}</div>
                </div>
              </div>
            ))}
          </figcaption>
        </figure>

        <div className="mt-16 flex items-end justify-between gap-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#D96C4A]">Mobile workspace</div>
            <h3 className="mt-2 font-display text-2xl font-semibold text-[#0A2424]">Keep the day moving from your phone.</h3>
          </div>
          <span className="hidden text-sm text-[#4B5E5E] sm:block">Swipe to explore →</span>
        </div>

        <div className="mt-8 overflow-x-auto pb-6">
          <div className="flex w-max items-start gap-5">
            {MOCKUP_SCREEN_ORDER.map((screen, index) => (
              <article
                key={screen.key}
                className="w-[208px] flex-none rounded-[1.6rem] border border-[#0E6B6B]/10 bg-white p-4 shadow-[0_12px_34px_rgba(10,36,36,0.06)]"
              >
                <div className="mb-4 flex min-h-[2.5rem] items-start justify-between gap-3">
                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#0E6B6B]">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="text-right text-sm font-semibold leading-snug text-[#0A2424]">{screen.label}</span>
                </div>
                <PhoneFrame src={screen.src} alt={`${screen.label} screen`} compact />
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductScreens;
